import Header from './components/Header'
import Hero from './components/Hero'
import Footer from './components/Footer'
import NewsSection from './NewsSection'
import SEO from './components/SEO'
import ShopSection from './components/ShopSection'

// Site configuration for glaciersday.com
const SITE_CONFIG = {
  name: 'Glaciers Day',
  description: 'Official Glacier Conservation Events',
  domain: 'glaciersday.com',
  
  hero: {
    title: 'Celebrate Glacier Day Worldwide',
    subtitle: 'Official Events & Conservation Initiatives',
    description: 'Join millions in celebrating Glacier Day through global events, conservation campaigns, and community activities. Be part of the movement to protect our planet\'s frozen treasures.',
    primaryButtonText: 'View Events Calendar',
    primaryButtonLink: '/events',
    secondaryButtonText: 'Organize Event',
    secondaryButtonLink: '/organize',
  },
  
  relatedSites: [
    {
      name: 'World Glaciers Data',
      description: 'Scientific data and glacier monitoring',
      domain: 'worldglaciersday.com',
    },
    {
      name: 'Glacier Education',
      description: 'Educational resources and awareness',
      domain: 'worlddayforglaciers.com',
    },
  ],
}

function App() {
  return (
    <>
      <SEO 
        title="Glaciers Day - Raising Awareness for Glacier Conservation"
        description="Official platform for glacier conservation events, climate change education, and environmental advocacy. Join the global movement to protect our glaciers."
        keywords="glaciers, climate change, environment, ice, polar regions, mountains, conservation,冰川,气候变化,环境,冰,极地,山区,保护"
        type="website"
      />
      <div className="min-h-screen flex flex-col">
      <Header 
        siteName={SITE_CONFIG.name}
        siteDescription={SITE_CONFIG.description}
      />
      
      <main className="flex-grow">
        <Hero {...SITE_CONFIG.hero} />
        
        <ShopSection />
        <NewsSection 
          website="glaciersday"
          limit={10000}
          title="Glacier Conservation News"
          themeColor="blue"
        />
        {/* Events Focus Section */}
        <section className="section-padding bg-glacier-50">
          <div className="container-padding mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-glacier-900 mb-4">
                Upcoming Glacier Day Events
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Join global celebrations, educational workshops, and conservation activities happening worldwide.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-xl shadow-lg border border-glacier-100">
                <div className="w-12 h-12 bg-glacier-100 rounded-lg flex items-center justify-center mb-6">
                  <span className="text-2xl">🎉</span>
                </div>
                <h3 className="text-xl font-bold text-glacier-900 mb-4">Global Celebrations</h3>
                <p className="text-gray-600 mb-4">
                  Participate in Glacier Day celebrations across 50+ countries with parades, exhibitions, and cultural events.
                </p>
                <a href="/celebrations" className="text-glacier-600 hover:text-glacier-800 font-medium">
                  View celebrations →
                </a>
              </div>
              
              <div className="bg-white p-8 rounded-xl shadow-lg border border-glacier-100">
                <div className="w-12 h-12 bg-glacier-100 rounded-lg flex items-center justify-center mb-6">
                  <span className="text-2xl">📋</span>
                </div>
                <h3 className="text-xl font-bold text-glacier-900 mb-4">Conservation Campaigns</h3>
                <p className="text-gray-600 mb-4">
                  Join signature campaigns, fundraising events, and volunteer programs to support glacier protection.
                </p>
                <a href="/campaigns" className="text-glacier-600 hover:text-glacier-800 font-medium">
                  Join campaigns →
                </a>
              </div>
              
              <div className="bg-white p-8 rounded-xl shadow-lg border border-glacier-100">
                <div className="w-12 h-12 bg-glacier-100 rounded-lg flex items-center justify-center mb-6">
                  <span className="text-2xl">🗓️</span>
                </div>
                <h3 className="text-xl font-bold text-glacier-900 mb-4">Event Calendar</h3>
                <p className="text-gray-600 mb-4">
                  Browse our comprehensive calendar of Glacier Day events, workshops, and activities happening near you.
                </p>
                <a href="/calendar" className="text-glacier-600 hover:text-glacier-800 font-medium">
                  View calendar →
                </a>
              </div>
            </div>
          </div>
        </section>
        
        {/* Contact Email Section - Prominent Display */}
        <section className="section-padding bg-gradient-to-r from-glacier-600 to-glacier-800 text-white">
          <div className="container-padding mx-auto max-w-7xl text-center">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Contact Us for Glacier Day Events
              </h2>
              <p className="text-xl text-glacier-200 mb-8">
                Have questions about Glacier Day events, partnerships, or media inquiries? 
                Reach out to our dedicated team.
              </p>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-10 border border-white/20">
                <div className="flex flex-col md:flex-row items-center justify-center gap-6">
                  <div className="flex items-center justify-center w-16 h-16 bg-white rounded-full">
                    <span className="text-3xl">📧</span>
                  </div>
                  <div className="text-center md:text-left">
                    <h3 className="text-2xl font-bold mb-2">Primary Contact Email</h3>
                    <a 
                      href="mailto:info@glaciersday.com" 
                      className="text-2xl md:text-3xl font-bold text-white hover:text-glacier-200 transition-colors inline-block"
                    >
                      info@glaciersday.com
                    </a>
                    <p className="text-glacier-200 mt-3">
                      For event coordination, sponsorship, media inquiries, and general questions
                    </p>
                  </div>
                </div>
                <div className="mt-8 pt-8 border-t border-white/20">
                  <p className="text-glacier-200">
                    <strong>Response Time:</strong> Within 24 hours • <strong>Languages:</strong> English, Spanish, French
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* History Section */}
        <section className="section-padding">
          <div className="container-padding mx-auto max-w-7xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-glacier-900 mb-6">
                  The History of Glacier Day
                </h2>
                <p className="text-gray-600 mb-6">
                  Established in 2010, Glacier Day has grown from a small local observance to a global movement celebrated in over 100 countries. What began as a single community's effort to raise awareness about glacier retreat has evolved into an international platform for climate action.
                </p>
                <p className="text-gray-600 mb-8">
                  Each year on December 13th, millions of people participate in events ranging from educational seminars in schools to large-scale conservation projects in glacier regions. The day serves as a reminder of the urgent need to protect these vital freshwater sources and the ecosystems they support.
                </p>
                <a href="/history" className="btn-primary">
                  Learn More About History
                </a>
              </div>
              <div className="bg-gradient-to-br from-glacier-100 to-glacier-200 rounded-2xl p-8">
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                      <span className="text-2xl">🌍</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-glacier-900">100+ Countries</h4>
                      <p className="text-gray-600 text-sm">Participating worldwide</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                      <span className="text-2xl">👥</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-glacier-900">5M+ Participants</h4>
                      <p className="text-gray-600 text-sm">Annual engagement</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                      <span className="text-2xl">📈</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-glacier-900">15 Years</h4>
                      <p className="text-gray-600 text-sm">Of glacier advocacy</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
      </main>
      
      <Footer 
        currentSite={{
          name: SITE_CONFIG.name,
          description: SITE_CONFIG.description,
          domain: SITE_CONFIG.domain,
        }}
        relatedSites={SITE_CONFIG.relatedSites}
      />
    </div>
    </>
  )
}

export default App